import { Fragment, useMemo, useState } from "react";

import {
  useCreatePartnerOrg,
  useDeletePartnerOrg,
  usePartnerOrgs,
  useSetPartnerOrgArchived,
  useUpdatePartnerOrg
} from "src/api/hooks";
import { ReadFailure } from "src/components/read-failure";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Empty,
  Field,
  Input,
  Select,
  Table
} from "src/components/ui";
import type {
  PartnerOrganizationFieldsFragment,
  PartnerOrganizationFieldsInput
} from "src/graphql/generated";
import { OrgPeoplePanel } from "src/screens/org-people";
import { canRead, canWrite, type Viewer } from "src/screens/types";

/**
 * Which rows the table shows.
 *
 * Three states rather than a checkbox, because "only the retired ones" is a real
 * question an operator asks — it is the list they scan to restore something — and a
 * two-state control can only answer it by mixing the retired rows back in with
 * everything else.
 *
 * `active` is the default. A retired firm is one we have stopped working with, so it
 * is not what somebody opening this screen came to see; until this filter existed the
 * whole table was shown and the retired rows were merely dimmed.
 */
export type ArchivedFilter = "active" | "archived" | "all";

/**
 * What the Organizations screen is looking at: which rows it shows, and whose roster
 * is open.
 *
 * Held by `App` rather than by this component, and that placement is the whole point.
 * Opening somebody from an expanded roster swaps this screen for their record, which
 * unmounts it — so state owned here is gone by the time Back returns, and the
 * operator lands on a collapsed table filtered back to "In use", having to find the
 * firm and re-open the roster for every person they check. Checking a headcount means
 * reading several people, so that round trip is the whole job.
 *
 * Deliberately not everything the screen holds. A half-typed edit row is a draft, and
 * a draft surviving a trip to somebody else's record and back would be a surprise
 * rather than a convenience; this is the reader's *place*, which is the thing worth
 * keeping.
 */
export type OrgsView = {
  archivedFilter: ArchivedFilter;
  /** The firm whose roster is open, or null — one at a time, for the reason the
   * expander itself gives. */
  expandedId: string | null;
};

/** Where the screen starts: every firm still in use, nothing expanded. */
export const INITIAL_ORGS_VIEW: OrgsView = { archivedFilter: "active", expandedId: null };

const ARCHIVED_FILTER_LABELS: Record<ArchivedFilter, string> = {
  active: "In use",
  archived: "Archived",
  all: "All"
};

/**
 * Filtered here rather than in the query, for the reason `assignableOrganizations`
 * gives for the pickers: one response feeds several readers wanting different
 * subsets of it.
 *
 * The listing's request also carries `partnerOrganizationsWithVisiblePeople` and the
 * headcounts, and those cover every firm, retired ones included — deliberately, since
 * INFRA-655 observed that a firm we have stopped working with is the likeliest one to
 * hold people whose engagement is over. A server-side filter would have to narrow
 * those too, breaking the drill-down, or leave them disagreeing with the list they
 * arrived with.
 *
 * It also keeps the filter instant, which is what makes defaulting retired firms out
 * of sight defensible: they are one click away. A toggle that refetched would make
 * the retired view slower than the thing it is a filter on. Worth revisiting if this
 * table outgrows a single page — it holds a couple of dozen rows.
 */
function applyArchivedFilter<T extends { archivedAt?: string | null }>(
  orgs: readonly T[],
  filter: ArchivedFilter
): T[] {
  if (filter === "all") {
    return [...orgs];
  }
  const wantArchived = filter === "archived";
  return orgs.filter((org) => Boolean(org.archivedAt) === wantArchived);
}

/** The two headcounts the listing carries, or null where nobody counted. */
type PeopleCounts = { readonly current: number; readonly ended: number } | null | undefined;

/**
 * The People cell: who is from this firm, at a glance.
 *
 * Both numbers are shown rather than only the current one, and the original reason is
 * live again: with one number the row would disagree with its own Delete button, since
 * a firm reading "1 current" is refused the delete on account of somebody who left.
 * Showing the split is what makes "None" and "delete is offered" line up.
 *
 * The deeper reason is the one INFRA-650 was filed for: "who is from this firm" was
 * being answered with a number that counted people who had left, and Danbi Lee's
 * report was not really "the count is wrong" but "the count disagrees with what I
 * know". Showing the split is what lets a reader reconcile it, and INFRA-655's
 * drill-down is what lets them check it. That is now the only job these numbers have.
 *
 * "None" rather than "0 current · 0 ended", which is a lot of characters for
 * "nobody", and rather than the dash, which is reserved for the genuinely different
 * case of not having been counted.
 */
function peopleSummary(counts: PeopleCounts): string {
  if (counts == null) {
    return "—";
  }
  const parts: string[] = [];
  if (counts.current > 0) {
    parts.push(`${counts.current} current`);
  }
  if (counts.ended > 0) {
    parts.push(`${counts.ended} ended`);
  }
  return parts.length > 0 ? parts.join(" · ") : "None";
}

/**
 * The one-paragraph explanation a reader gets in place of the management controls.
 *
 * It sits where the Add card would be rather than replacing each button, so the
 * explanation is given once. What it must not do is let the controls simply vanish:
 * a button rendered as blank space is indistinguishable from a broken screen, and
 * that failure is a live bug elsewhere in this console (INFRA-664).
 *
 * It says which access is missing and why that is the access involved, because the
 * reader may well hold write access — an on-demand contractor writer does — and
 * "you cannot write" would then read as wrong rather than as a rule.
 *
 * Titled for what it is about rather than reusing "Add an organization", which would
 * head a card that cannot add one.
 */
function ManageNotice() {
  return (
    <Card title="Managing organizations">
      <p className="text-sm text-slate-600">
        You can see this list but not change it. An organization here is the firm a partner is
        engaged through, so adding one, renaming one, archiving and restoring one, or deleting one
        nothing has ever used takes write access to partners. On-demand contractors work for Need
        directly and are never recorded against a firm, so write access to them does not cover this
        list.
      </p>
      <p className="mt-2 text-sm text-slate-600">
        Ask an administrator if maintaining these firms is part of your work.
      </p>
    </Card>
  );
}

/**
 * External partner organizations — the firms people are engaged through.
 *
 * Managed by a partner writer, or an iam-admin, who holds partner write access as
 * well. Not by any writer in either population, which was the rule until INFRA-659:
 * INFRA-621 refused an organization to an on-demand contractor outright, so a
 * contractor-only writer draws nothing from this list and had no business renaming
 * what is on it. Reading is open to every reader, deliberately and unchanged — the
 * create form's own lookups already name every one of these firms to everyone, so
 * withholding the listing would withhold no firm from anybody. What it adds on top is
 * two timestamps and a headcount; the reasoning for each is on `ListPartnerOrgs`.
 *
 * The Owner column is the exception, and the reason it is one is the reason that
 * sentence had to be rewritten: it used to say the rows carry no personal data, and
 * INFRA-693 put a person on them. A relationship owner is a name on our side, which
 * lookups does not hand out, so the server serves it separately and only to a reader
 * with partner access — and this screen leaves the column out rather than rendering it
 * empty, because an empty cell would claim nobody is recorded.
 *
 * Presentation only, as ever: the backend refuses these writes regardless, and that is
 * the actual guarantee. What this side owes is an explanation for what is missing.
 */
export function PartnerOrgs({
  viewer,
  view,
  onView,
  onOpen,
  onAddPerson
}: {
  viewer: Viewer;
  /**
   * Which rows to show and whose roster is open, owned by `App` so a round trip to a
   * person's record does not collapse the table. See `OrgsView`.
   */
  view: OrgsView;
  onView: (next: OrgsView) => void;
  /** Opens a person's record, so an expanded roster can be checked against it. */
  onOpen: (identityId: string) => void;
  /**
   * Adds an existing person to this firm. The name travels with the id because what the
   * panel does with it is say which firm it is scoped to, and looking it back up
   * there would mean passing the whole listing across as well.
   *
   * Required rather than optional: an optional callback that silently hides the
   * control is the failure `ManageNotice` below exists to avoid, and required is what
   * makes the compiler ask App for it.
   */
  onAddPerson: (organization: { id: string; name: string }) => void;
}) {
  // The partner population specifically, not "writes anything anywhere". Named
  // canManage because that is the question the screen asks of it.
  const canManage = canWrite(viewer, "PARTNER");
  // Whether the Owner column exists at all. Every writer role includes read in its own
  // population, so this is never false while canManage is true — it is asked as its own
  // question because it is a different one, and the server answers it independently.
  const canSeeOwner = canRead(viewer, "PARTNER");
  const { data, isPending, error } = usePartnerOrgs();
  const [editingId, setEditingId] = useState<string | null>(null);
  const { archivedFilter, expandedId } = view;
  // The row a confirmation is open for, held as the whole row rather than an id: the
  // dialog names the firm, and looking it back up would render an empty sentence for
  // the one frame between a successful delete and the refetch that removes the row.
  const [deleting, setDeleting] = useState<PartnerOrganizationFieldsFragment | null>(null);
  const remove = useDeletePartnerOrg();
  const setArchived = useSetPartnerOrgArchived();

  const allOrgs = data?.externalPartnerOrganizations ?? [];
  const orgs = useMemo(
    () => applyArchivedFilter(allOrgs, archivedFilter),
    [allOrgs, archivedFilter]
  );
  // What the filter is keeping back, so the count can say so. Reported as a number
  // rather than left to be inferred from a short list: an operator who cannot find a
  // firm they know exists needs to be told it is filtered out, not left to wonder
  // whether it was ever added.
  const hidden = allOrgs.length - orgs.length;
  // Which rows lead somewhere, answered by the server in the same request as the
  // listing.
  //
  // Deliberately not derived from the viewer's populations. The counts are a total
  // across both and a list of names cannot be, so a reader can face a count with
  // nothing to expand — but "no partner access means nothing to see" is not true of
  // the rows written before an on-demand contractor was refused a firm, and hiding
  // those would hide them from their only readers. Asked as a question about the
  // data, the answer is right in both cases.
  const drillable = useMemo(
    () => new Set(data?.partnerOrganizationsWithVisiblePeople ?? []),
    [data?.partnerOrganizationsWithVisiblePeople]
  );
  // Who owns each relationship, answered by the server in the same request. A firm
  // with nobody recorded is absent rather than present with an empty string, and a
  // reader without partner access receives nothing at all — which is why the column
  // is hidden from them rather than shown full of dashes.
  const owners = useMemo(
    () =>
      new Map(
        (data?.partnerOrganizationRelationshipOwners ?? []).map((o) => [
          o.organizationId,
          o.relationshipOwner
        ])
      ),
    [data?.partnerOrganizationRelationshipOwners]
  );
  // Built rather than written out twice, now that two separate permissions each add a
  // column: the edit row and the expanded roster both have to span exactly this many.
  const head = [
    "Name",
    ...(canSeeOwner ? ["Owner"] : []),
    "People",
    "Updated",
    ...(canManage ? [""] : [])
  ];

  return (
    <div className="flex flex-col gap-4">
      {canManage ? <CreateOrgCard canSeeOwner={canSeeOwner} /> : <ManageNotice />}

      <Card
        title={`Organizations (${orgs.length})`}
        actions={
          // An inline label rather than a Field, which stacks its label above the
          // control: the card header is one thin row shared with the title, and a
          // stacked field there would push it to two.
          <label className="flex items-center gap-2 text-sm">
            <span className="font-medium text-slate-700">Show</span>
            <Select
              value={archivedFilter}
              onChange={(e) =>
                // The open roster is dropped rather than carried across, because the
                // filter can hide the row it belongs to: a roster expanded under a
                // firm that "Archived" then filters out would stay mounted, fetching
                // and rendering under no visible row.
                onView({ archivedFilter: e.target.value as ArchivedFilter, expandedId: null })
              }
            >
              {(Object.keys(ARCHIVED_FILTER_LABELS) as ArchivedFilter[]).map((value) => (
                <option key={value} value={value}>
                  {ARCHIVED_FILTER_LABELS[value]}
                </option>
              ))}
            </Select>
          </label>
        }
      >
        {isPending ? <Empty>Loading…</Empty> : null}
        {error ? <ReadFailure error={error} /> : null}
        {!isPending && !error && orgs.length === 0 ? (
          // Two different facts, and conflating them is what sends somebody looking
          // for a firm that is right there behind the filter.
          <Empty>
            {allOrgs.length === 0
              ? "No organizations yet."
              : `No organizations match “${ARCHIVED_FILTER_LABELS[archivedFilter]}”. Showing All lists ${allOrgs.length}.`}
          </Empty>
        ) : null}

        {orgs.length > 0 ? (
          // The actions column is dropped rather than left empty for a reader. A
          // column of blank cells under a blank header is the same failure the notice
          // above exists to avoid, repeated once per row — and unlike the Add card
          // there is nothing useful to say in it that the notice has not said.
          <Table head={head}>
            {orgs.map((org) => {
              const archived = Boolean(org.archivedAt);
              const expanded = expandedId === org.id;
              return editingId === org.id ? (
                <EditOrgRow
                  key={org.id}
                  org={org}
                  owner={owners.get(org.id) ?? ""}
                  canSeeOwner={canSeeOwner}
                  columns={head.length}
                  onDone={() => setEditingId(null)}
                />
              ) : (
                // A Fragment because the roster is a second row of the same table
                // rather than something nested inside this one: a panel inside a cell
                // would be as wide as the People column.
                <Fragment key={org.id}>
                  <tr className="border-b border-slate-100 align-top">
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Dimmed as well as labelled: the label says what the state
                          is, the dimming makes the row scannable as retired without
                          reading it. slate-500 rather than slate-400, which was
                          2.6:1 on white — the firm's name is the row's subject, and
                          dimming it must not mean nobody can read it. */}
                        <span className={archived ? "text-slate-500" : undefined}>{org.name}</span>
                        {/* Badge rather than a copy of it. The copy existed only to
                            carry a `title`, and a tooltip is invisible to touch and
                            unreachable by keyboard — so it was never the way to say
                            this, and putting one on the shared Badge would spread a
                            mechanism that does not work. Badge's slate-700 is darker
                            than the copy's slate-600, so the contrast the copy was
                            tuned for improves rather than regresses.

                            The date the tooltip carried is now beside the label, where
                            everyone can read it rather than only somebody hovering a
                            mouse. What is not restored is the sentence explaining what
                            archiving *means*: that is the same on every row, so it is
                            not row information, and repeating it per row is the kind of
                            helper prose this form keeps shedding. `archived` is
                            `Boolean(archivedAt)`, so the date is there whenever the
                            label is. */}
                        {archived ? (
                          <>
                            <Badge>Archived</Badge>
                            <span className="text-xs text-slate-500">
                              {new Date(org.archivedAt as string).toLocaleDateString()}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </td>
                    {canSeeOwner ? (
                      // Free text, so it renders as typed. A dash where nobody is
                      // recorded, matching Updated below: this is a fact we do not hold
                      // rather than an empty one, and most firms will read this way
                      // until somebody fills them in.
                      <td className="px-2 py-2 text-xs text-slate-600">
                        {owners.get(org.id) ?? "—"}
                      </td>
                    ) : null}
                    <td className="px-2 py-2 text-xs text-slate-600">
                      {/* The number itself is the control, because it is the thing being
                        questioned — Danbi Lee's report was a count she could not
                        reconcile, and the fix for that is being able to open it.

                        Plain text where there is nobody this reader can see. An
                        expander opening on an empty panel would be a dead end on
                        every row, and "you cannot see these people" is not even what
                        is true: no on-demand contractor is engaged through a firm, so
                        for an odc-only reader there is usually nothing there to
                        withhold. */}
                      {drillable.has(org.id) ? (
                        <button
                          type="button"
                          className="underline hover:text-slate-900"
                          aria-expanded={expanded}
                          // One open panel at a time. Several would each hold a
                          // roster, which is the payload the on-demand fetch exists to
                          // avoid, and nobody compares two firms side by side on a
                          // screen whose job is checking one number.
                          onClick={() => onView({ ...view, expandedId: expanded ? null : org.id })}
                        >
                          {peopleSummary(org.peopleCounts)}
                        </button>
                      ) : (
                        peopleSummary(org.peopleCounts)
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-slate-500">
                      {org.updatedAt ? new Date(org.updatedAt).toLocaleDateString() : "—"}
                    </td>
                    {canManage ? (
                      <td className="px-2 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Archived organizations cannot be newly assigned. */}
                          {archived ? null : (
                            <Button
                              variant="secondary"
                              data-add-person-at={org.id}
                              // Every row says "Add a person", which is ambiguous to
                              // anyone moving between controls by name. The visible
                              // text is contained in this, so the label-in-name rule
                              // still holds.
                              aria-label={`Add a person at ${org.name}`}
                              onClick={() => onAddPerson({ id: org.id, name: org.name })}
                            >
                              Add a person
                            </Button>
                          )}
                          <Button variant="secondary" onClick={() => setEditingId(org.id)}>
                            Edit
                          </Button>
                          {/* No confirmation dialog, unlike Delete beside it — and that
                            contrast is the reason rather than a coincidence. Archiving
                            changes nothing but which dropdowns offer the row, and is
                            undone by this same button, so a confirm would be friction
                            guarding an action with nothing to warn about. Delete has one
                            because nothing undoes it. The comparison was dropped while
                            INFRA-656 had retired that button and is restored with it. */}
                          <Button
                            variant="secondary"
                            disabled={setArchived.isPending}
                            onClick={() => setArchived.mutate({ id: org.id, archived: !archived })}
                          >
                            {archived ? "Restore" : "Archive"}
                          </Button>
                          {/* Offered only where the headcount is zero, which is one of
                            the server's two conditions and the only one the console can
                            see — a firm can read "None" and still be refused, because a
                            term recorded through it was later moved elsewhere and the
                            audit trail remembers. So this hides a button that would
                            certainly fail and never promises one that will succeed; the
                            confirmation says as much, and the server is the guarantee.

                            Hidden rather than disabled, unlike the notice above: a
                            disabled Delete on every firm with anybody through it would
                            put a permanently dead control on most rows of the table,
                            and the paragraph below the table explains the rule once. */}
                          {org.peopleCounts &&
                          org.peopleCounts.current + org.peopleCounts.ended === 0 ? (
                            <Button
                              variant="danger"
                              disabled={remove.isPending}
                              onClick={() => setDeleting(org)}
                            >
                              Delete
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                  {expanded ? (
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {/* Spanning every column so the roster reads as an explanation of
                        the whole row rather than of one cell — however many that is,
                        since a reader gets no actions column and only partner access
                        gets an Owner one. */}
                      <td className="px-2 py-3" colSpan={head.length}>
                        <OrgPeoplePanel
                          organizationId={org.id}
                          // The number this panel is being asked to explain, so it can
                          // say whether it is showing all of it. Null where the listing
                          // did not count, which the panel treats as unknown rather
                          // than as zero.
                          headcount={
                            org.peopleCounts
                              ? org.peopleCounts.current + org.peopleCounts.ended
                              : null
                          }
                          onOpen={onOpen}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </Table>
        ) : null}

        <p className="mt-3 text-xs text-slate-500">
          Add a person lets you choose an existing partner and assign one of their engagements to
          this organization. To change or clear an assignment, open the person&rsquo;s record and
          edit the engagement.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Archiving is for a firm we have stopped working with: it stops being offered when
          recording a new engagement, and nothing else changes — every term already recorded through
          it keeps its organization, and history keeps naming it. Restore puts it back. Renaming it
          to something like &ldquo;(do not use)&rdquo; is not the way to retire one: history shows a
          firm&rsquo;s current name, so that would rewrite how every past term through it reads.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Delete is for a firm added by mistake, and only that: it is refused for any organization a
          term has ever been recorded through, even one that ended long ago. A person&rsquo;s
          history names their firm by pointing at this row, so removing it would leave that record
          referring to something nothing can name. A firm still showing people is not offered a
          Delete at all; one showing none may still be refused, because a term recorded through it
          was later moved elsewhere and the history remembers. Archive those instead, or correct a
          wrong entry by editing it.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          The People count opens, where there is anybody in it you can read, into who those people
          are — current and ended, labelled the same way, each linking to their record. It is not
          offered on a firm whose people are all in a population you cannot read; the count itself
          is a total across both, so it can be larger than the list, and the list says so when it
          is.
        </p>
        {canSeeOwner ? (
          <p className="mt-2 text-xs text-slate-500">
            Owner is who <em>here</em> to contact about the firm. It is free text and nobody has to
            hold a console login to be named, so it records whoever actually deals with them; edit a
            firm to change it. It is not a contact <em>at</em> the firm — recording one of those is
            an open question with Legal: an owner is one of us, while somebody at the firm is not,
            and we would be holding their name and how to reach them without their involvement.
          </p>
        ) : null}
        {hidden > 0 ? (
          <p className="mt-2 text-xs text-slate-500">
            {hidden} organization{hidden === 1 ? " is" : "s are"} hidden by the Show filter.
          </p>
        ) : null}
      </Card>

      {/* A confirmation, unlike Archive, and the asymmetry is the whole difference
        between the two actions: archiving is undone by the button beside it, and this
        cannot be undone at all. One dialog outside the table rather than one per row. */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this organization?"
        confirmLabel="Delete"
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) {
            return;
          }
          remove.mutate({ id: deleting.id }, { onSuccess: () => setDeleting(null) });
        }}
      >
        <p>
          <strong>{deleting?.name}</strong> will be removed permanently. There is no undo.
        </p>
        <p className="mt-2">
          It is refused if any engagement has ever been recorded through this firm, including one
          since moved to a different firm — so it may not go through even though the row shows
          nobody. Archive it instead if it does.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function CreateOrgCard({ canSeeOwner }: { canSeeOwner: boolean }) {
  const create = useCreatePartnerOrg();
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");

  const ready = name.trim() !== "";

  return (
    <Card
      title="Add an organization"
      actions={
        <Button
          disabled={!ready || create.isPending}
          onClick={() =>
            create.mutate(
              {
                name: name.trim(),
                // Omitted when blank rather than sent as an empty string, so a firm
                // added without one is recorded as having nobody rather than as having
                // been given an answer. The backend treats the two alike; the payload
                // is where the difference is legible.
                ...(owner.trim() ? { relationshipOwner: owner.trim() } : {})
              },
              {
                onSuccess: () => {
                  setName("");
                  setOwner("");
                }
              }
            )
          }
        >
          {create.isPending ? "Adding…" : "Add"}
        </Button>
      }
    >
      {/* Two columns again. The grid was dropped when INFRA-671 removed the alias box
          and left a lone input stretched across half a card, reading as a form missing
          its other half; INFRA-693 gives it a second field, so the layout the card was
          built with is the one that fits. */}
      <div className="grid max-w-3xl grid-cols-1 gap-3 md:grid-cols-2">
        {/* The name is the organization's identity, so the backend refuses a second
            one spelled the same in any case. Said here rather than left to the
            round trip, which is where the operator would otherwise learn it. */}
        <Field label="Name" hint="The firm's name, as it should read. Must be unique.">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {canSeeOwner ? (
          // Optional, and the hint says so: the person who records a new firm is often
          // not the person who will own the relationship with it, so this is the field
          // most likely to be left for later. Add stays enabled without it.
          <Field label="Relationship owner" hint="Optional. Who here to contact about this firm.">
            <Input value={owner} onChange={(e) => setOwner(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Card>
  );
}

function EditOrgRow({
  org,
  owner,
  canSeeOwner,
  columns,
  onDone
}: {
  org: PartnerOrganizationFieldsFragment;
  /** The recorded relationship owner, or "" where there is none. */
  owner: string;
  canSeeOwner: boolean;
  /**
   * How many columns the table has, so this row covers the one it replaces. Passed in
   * rather than recomputed: two separate permissions each add a column, and a second
   * copy of that arithmetic is a second place for it to go wrong.
   */
  columns: number;
  onDone: () => void;
}) {
  const update = useUpdatePartnerOrg();
  /**
   * What the row opened with, frozen at mount, which is what the patch below is a
   * diff against.
   *
   * It has to be held rather than read off the props, and that is the whole of the
   * second half of the #11733 review finding. The props keep arriving: every partner
   * write invalidates the `partnerOrgs` key, and the Add card and the other rows'
   * actions are all still on screen while this one is open, so a refetch lands under
   * it routinely. Diffing against a prop that has moved makes a box nobody touched
   * "differ" from it, and the diff has no way to tell that from an edit — so the row
   * would send an explicit replacement for a field its operator never saw, which is
   * the lost update the diff was introduced to stop.
   *
   * One `useState` initialiser rather than a `useRef`, so the freeze is the same
   * mechanism as the boxes below rather than a second one. The row is keyed on the
   * organization's id, so opening Edit elsewhere mounts a fresh one.
   *
   * `identity-detail.tsx`'s engagement panel is the near neighbour and had the same
   * bug; INFRA-709 closed it there the same way, so the two rows now agree.
   */
  const [opened] = useState({ name: org.name, owner });
  const [name, setName] = useState(opened.name);
  const [relationshipOwner, setRelationshipOwner] = useState(opened.owner);

  const ready = name.trim() !== "";

  /**
   * What changed, not what is on screen, which is the whole of the input's pointer
   * convention put to use: an omitted field means "leave it alone", and the store's
   * locked read-modify-write exists so two operators editing different fields do not
   * overwrite each other.
   *
   * Sending both values on every save defeated that, and the review of #11733 found
   * it: a rename saved while somebody else had changed the owner wrote the stale one
   * back — an explicit replacement as far as the store can tell, audited as an
   * ordinary edit. The lock cannot help, because a stale value and a deliberate one
   * arrive identical.
   *
   * Compared against `opened` rather than against the props, which is the half the
   * first fix got wrong — see above. Clearing stays expressible either way, and that
   * is what sending everything was for: an emptied box differs from a non-empty
   * original, so it is sent as "" — while an empty box that was always empty is not
   * sent at all.
   */
  const fields: PartnerOrganizationFieldsInput = {};
  if (name.trim() !== opened.name) {
    fields.name = name.trim();
  }
  // Never sent where the column was not rendered: a field this caller could not see
  // must not be written from a box they were never given.
  if (canSeeOwner && relationshipOwner.trim() !== opened.owner) {
    fields.relationshipOwner = relationshipOwner.trim();
  }

  return (
    <tr className="border-b border-slate-100 bg-slate-50 align-top">
      {/* The inputs span every column but the actions one: a name input sized to the
        Name column alone would be narrower than several of the firm names it has to
        hold, and the owner box sits beside it rather than under its own heading. */}
      <td className="px-2 py-2" colSpan={columns - 1}>
        <div className="flex flex-wrap gap-2">
          {/* Labelled, unlike the single input this replaces, because two unlabelled
            boxes side by side are indistinguishable to anyone who is not looking at
            them. Not "Name", which would collide with the Add card's field above. */}
          <Input
            aria-label="Organization name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full max-w-md"
          />
          {canSeeOwner ? (
            <Input
              aria-label="Relationship owner"
              placeholder="Relationship owner"
              value={relationshipOwner}
              onChange={(e) => setRelationshipOwner(e.target.value)}
              className="w-full max-w-xs"
            />
          ) : null}
        </div>
      </td>
      <td className="px-2 py-2">
        <div className="flex gap-2">
          <Button
            disabled={!ready || update.isPending}
            onClick={() => update.mutate({ id: org.id, fields }, { onSuccess: onDone })}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * The inline form on the create-a-person screen, so that discovering a firm is
 * missing does not mean abandoning a half-filled form to go add it.
 *
 * The same field as the management card since INFRA-671 removed aliases, which used
 * to be the difference between them. Kept as its own component rather than folded
 * into that one: it renders inline inside another form, selects what it creates, and
 * asks a different permission question — see below.
 *
 * It takes no viewer and asks no permission question, which since INFRA-659 rests on
 * something its one call site guarantees: the create form renders it only when the
 * chosen population is not on-demand contractor, and the population picker offers only
 * populations the caller may write in. So reaching this form means holding partner
 * write access, and an odc-only writer never sees it — the field itself is hidden for
 * that population, with a line saying why, since INFRA-621.
 *
 * If it is ever rendered somewhere that does not establish both, it needs the check
 * that the management card has. The backend refuses either way; what would be lost is
 * the explanation, leaving an operator with a form that only fails on submit.
 */
export function InlineCreateOrg({ onCreated }: { onCreated: (id: string) => void }) {
  const create = useCreatePartnerOrg();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  if (!open) {
    return (
      <button
        type="button"
        className="self-start text-xs font-medium text-slate-600 underline hover:text-slate-900"
        onClick={() => setOpen(true)}
      >
        Not listed? Add one
      </button>
    );
  }

  const ready = name.trim() !== "";

  return (
    <div className="flex flex-col gap-2 rounded-md border border-slate-300 bg-slate-50 p-2">
      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="flex gap-2">
        <Button
          disabled={!ready || create.isPending}
          onClick={() =>
            create.mutate(
              { name: name.trim() },
              {
                onSuccess: (result) => {
                  // Select it straight away: the reason for adding it here is to use
                  // it on this form, and the lookup refetch would otherwise leave the
                  // picker on "Independent individual".
                  onCreated(result.createPartnerOrganization.id);
                  setOpen(false);
                  setName("");
                }
              }
            )
          }
        >
          {create.isPending ? "Adding…" : "Add and select"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setOpen(false);
            setName("");
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
